if (!customElements.get('product-form')) {
    customElements.define(
        'product-form',
        class ProductForm extends HTMLElement {
            constructor() {
                super();

                this.form = this.querySelector('form');
                this.form.querySelector('[name=id]').disabled = false;
                this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
                this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
                this.submitButton = this.querySelector('[type="submit"]');

                if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

                this.hideErrors = this.dataset.hideErrors === 'true';
            }

            connectedCallback(){
                this.productVariantId = this.form.elements['id'].value;

                // Max sellable quantity, from server, depending on pre-order and locations
                const rawQuantityMax = this.dataset.inventoryQuantityMax;
                this.inventoryQuantityMax = rawQuantityMax != null && rawQuantityMax !== ''
                    ? parseInt(rawQuantityMax, 10)
                    : undefined;
                this.inventoryQuantityErrorMessage = this.dataset.inventoryQuantityErrorMessage;

                // Inventory quantity, on hand + pre-orderable. On hand can be the sum of location inventories
                const rawQuantityStocked = this.dataset.inventoryQuantity;
                this.inventoryQuantity = rawQuantityStocked != null && rawQuantityStocked !== ''
                    ? parseInt(rawQuantityStocked, 10)
                    : undefined;
                // Inventory at market-specific location. When more locations can fulfill the order, the inventoryQuantity is the sum of all location available to fulfill.
                // This value is needed to know if we are going to fulfill from more locations.
                const rawLocationInventory = this.dataset.locationQuantity;
                this.locationInventory = rawLocationInventory != null && rawLocationInventory !== ''
                    ? parseInt(rawLocationInventory,10)
                    : undefined

                const rawPreOrderDeliveryDate = this.dataset.preOrderDate;
                this.preOrderDeliveryDate = rawPreOrderDeliveryDate != null && rawPreOrderDeliveryDate !== ''
                    ? rawPreOrderDeliveryDate
                    : undefined

                const rawCanBeFromEu = this.dataset.fromEu;
                this.canBeFromEu = rawCanBeFromEu != null && rawCanBeFromEu === 'true';
            }
            async onSubmitHandler(evt) {
                evt.preventDefault();
                if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

                const formData = new FormData(this.form);

                this.handleErrorMessage();

                // We may have line items splitted, so we do the sum of all the lines with the same variant ID
                const cartItems = await fetch(`${routes.cart_url}.json`)
                    .then((res) => res.json())
                    .then((res) => res.items?.filter(item => item['id'] == formData.get('id')) || []);

                let stockQtyInCart = 0;
                let preorderQtyInCart = 0;
                let fromEuQtyInCart = 0;

                for (const item of cartItems) {
                    if (item?.properties && item.properties["Pre-order"]) {
                        preorderQtyInCart += item.quantity;
                    } else if (item?.properties && item.properties["Shipping in"]) {
                        fromEuQtyInCart += item.quantity;
                    } else {
                        stockQtyInCart += item.quantity;
                    }
                }

                const totalQuantityInCart = preorderQtyInCart + stockQtyInCart + fromEuQtyInCart;

                const productInCartQuantity = totalQuantityInCart || 0;
                const quantityToAdd = parseInt(formData.get('quantity')) || 1;

                if (
                    Number.isInteger(this.inventoryQuantityMax) &&
                    quantityToAdd + productInCartQuantity > this.inventoryQuantityMax
                ) {
                    this.handleErrorMessage(this.inventoryQuantityErrorMessage);
                    return;
                }

                const price = parseInt(this.dataset.price, 10);

                if (price === 0) {
                    return;
                }

                this.submitButton.setAttribute('aria-disabled', true);
                this.submitButton.classList.add('loading');
                this.querySelector('.loading__spinner').classList.remove('hidden');

                let config = fetchConfig('javascript');
                config.headers['X-Requested-With'] = 'XMLHttpRequest';
                delete config.headers['Content-Type'];

                if (this.cart) {
                    formData.append(
                        'sections',
                        this.cart.getSectionsToRender().map((section) => section.id),
                    );
                    formData.append('sections_url', window.location.pathname);
                    this.cart.setActiveElement(document.activeElement);
                }
                // Manage Pre order logic
                const totalAfterAdd = quantityToAdd + productInCartQuantity;
                let normalQty = 0;
                let preorderQty = 0;
                let fromEUQty = 0;

                let remainingStock = this.inventoryQuantity - stockQtyInCart;
                if (this.canBeFromEu){
                    remainingStock = this.locationInventory - stockQtyInCart;
                }
                const remainingPreorder = this.inventoryQuantityMax - this.inventoryQuantity - preorderQtyInCart;
                const remainingFromEu = this.inventoryQuantityMax - this.locationInventory - fromEuQtyInCart;

                if (remainingStock > 0) {
                    normalQty = Math.min(quantityToAdd, remainingStock);
                }

                const remainingToAdd = quantityToAdd - normalQty;
                if (remainingToAdd > 0 && remainingPreorder > 0 && !this.canBeFromEu) {
                    preorderQty = Math.min(remainingToAdd, remainingPreorder);
                }
                if (remainingToAdd > 0 && remainingFromEu > 0 && this.canBeFromEu){
                    fromEUQty = Math.min(remainingToAdd, remainingFromEu);
                }

                const items = [];
                let addToCartPayload = {};
                let specialProperties = null;
                let specialQuantity = 0;
                if (preorderQty > 0 && !this.canBeFromEu) {
                    specialProperties = { "Pre-order": this.preOrderDeliveryDate };
                    specialQuantity = preorderQty;
                }
                if (fromEUQty > 0 && this.canBeFromEu) {
                    specialProperties = { "Shipping in": "20 days" };
                    specialQuantity = fromEUQty;
                }

                if (specialProperties && specialQuantity > 0) {
                    items.push({
                        id: this.productVariantId,
                        quantity: specialQuantity,
                        properties: specialProperties,
                    })

                    if (normalQty > 0) {
                        items.push({
                            id: this.productVariantId,
                            quantity: normalQty
                        });
                    }

                    if (this.cart){
                        addToCartPayload = {
                            items,
                            sections: this.cart.getSectionsToRender().map((section) => section.id),
                            sections_url: window.location.pathname
                        }
                    }

                    config = {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Accept': 'application/json',
                            'X-Requested-With': 'XMLHttpRequest'
                        },
                        body: JSON.stringify(addToCartPayload)
                    };
                } else config.body = formData;

                fetch(`${routes.cart_add_url}.js`, config)
                    .then((response) => response.json())
                    .then((response) => {
                        if (response.status) {
                            publish(PUB_SUB_EVENTS.cartError, {
                                source: 'product-form',
                                productVariantId: formData.get('id'),
                                errors: response.errors || response.description,
                                message: response.message,
                            });
                            this.handleErrorMessage(response.description);

                            const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
                            if (!soldOutMessage) return;
                            this.submitButton.setAttribute('aria-disabled', true);
                            this.submitButton.querySelector('span').classList.add('hidden');
                            soldOutMessage.classList.remove('hidden');
                            this.error = true;
                            return;
                        } else if (!this.cart) {
                            window.location = window.routes.cart_url;
                            return;
                        }

                        if (!this.error)
                            publish(PUB_SUB_EVENTS.cartUpdate, {
                                source: 'product-form',
                                productVariantId: formData.get('id'),
                                cartData: response,
                            });
                        this.error = false;
                        const quickAddModal = this.closest('quick-add-modal');
                        if (quickAddModal) {
                            document.body.addEventListener(
                                'modalClosed',
                                () => {
                                    setTimeout(() => {
                                        this.cart.renderContents(response);
                                    });
                                },
                                { once: true },
                            );
                            quickAddModal.hide(true);
                        } else {
                            this.cart.renderContents(response);
                        }
                    })
                    .catch((e) => {
                        console.error(e);
                    })
                    .finally(() => {
                        this.submitButton.classList.remove('loading');
                        if (this.cart && this.cart.classList.contains('is-empty'))
                            this.cart.classList.remove('is-empty');
                        if (!this.error) this.submitButton.removeAttribute('aria-disabled');
                        this.querySelector('.loading__spinner').classList.add('hidden');
                    });
            }

            handleErrorMessage(errorMessage = false) {
                if (this.hideErrors) return;

                this.errorMessageWrapper =
                    this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
                if (!this.errorMessageWrapper) {
                    if (!errorMessage) return;
                    // Here we show Sold Out message with enlarged add to cart button for 2.5s
                    this.submitButton.classList.add('disabled')
                    this.submitButton.setAttribute('disabled',true);
                    const buttonCanEnlargeByDefault = this.form.classList.contains('enlarge');
                    if (!buttonCanEnlargeByDefault) {
                        this.form.classList.add('enlarge');
                    }
                    this.classList.add('has_error');
                    setTimeout(() => {
                    this.classList.remove('has_error');
                        if (!buttonCanEnlargeByDefault) this.form.classList.remove('enlarge');
                    }, 2500);
                    return;
                }
                this.errorMessage =
                    this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

                this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

                if (errorMessage) {
                    this.errorMessage.textContent = errorMessage;
                }
            }

            hasMatchingProperties(target = {}, reference = {}) {
                return Object.entries(reference).every(
                    ([key, val]) => target?.[key] === val
                );
            }
        },
    );
}
